/*
    By default, only state.config.settings will definitely exist when the application starts. The rest of the config is loaded from a file.

    So, for selectors on ALL properties nested within settings, make sure you use optional chaining with the ? syntax.

    Also, when you retrieve data with useSelector() inside a JSX file, make sure to use optional chaining there, too.
    i.e.
    const selectedExpressSettings = useSelector(selectConfigExpressSettings);
    ...
    selectedExpressSettings?.verbiage <-- the property may not exist yet, so use the ? chain and it will appear when it is observed (i.e. it exists)
*/

import { C_DEFAULT_TAX_CERTIFICATION } from '../../../common/consts';
import {
  beginsWithVowel,
  isNullUndefinedOrEmptyObject,
} from '../../../common/utils';
import {
  MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM,
  TAX_PAYMENT_PERIOD_TYPES_ENUM,
} from '../../../common/enums';
import { RentalType } from '../../../common/consts';
import { RootState } from 'src/store/store';
import { CollectionAgreementBucketWith } from 'src/types/tax';
import { TaxConfiguration } from 'src/types/configuration';
import { resolveRentalStructureOptions } from 'src/common/rentalStructureResolver';

// GENERAL CONFIG SELECTORS
export const selectIsExpressConfigLoaded = (state: RootState) =>
  !isNullUndefinedOrEmptyObject(state?.config?.settings?.express);

export const selectConfigSharedPermitAccess = (state: RootState) =>
  state.config?.settings?.sharedPermitAccess ?? false;
export const selectConfigLoadFailed = (state: RootState) =>
  state.config.configLoadFailed;
export const selectConfigSettings = (state: RootState) =>
  state.config.settings || {};
export const selectConfigExpressSettings = (state: RootState) =>
  state.config.settings?.express;
export const selectConfigExpressSettingsLogo = (state: RootState) =>
  state.config.settings?.express?.logo;
export const selectConfigExpressSettingsName = (state: RootState) => {
  return (
    state.config.settings?.express?.registration?.place_display_name ??
    state.config.settings?.placeDisplayName
  );
};
export const selectConfigExpressRegistrationPlace = (state: RootState) =>
  state.config.settings?.place;
export const selectConfigExpressApplyParcel = (state: RootState) =>
  state.config.settings?.express?.parcel;
export const selectConfigExpressVerbiage = (state: RootState) =>
  state.config.settings?.express?.verbiage;
export const selectConfigExpressLandingAddendum = (state: RootState) =>
  state.config.settings?.express?.landing_addendum;
export const selectConfigExpressParcelVerbiage = (state: RootState) =>
  state.config.settings?.express?.parcel?.verbiage || '';
export const selectConfigExpressRegistrationTypeVerbiage = (state: RootState) =>
  state.config.settings?.express?.registration?.type_verbiage || '';
export const selectConfigExpressRegistrationVerbiage = (state: RootState) =>
  state.config.settings?.express?.registration?.verbiage;
export const selectConfigExpressLocalContactVerbiage = (state: RootState) =>
  state.config.settings?.express?.registration?.local_contact_verbiage || '';
export const selectJurisdictionEmail = (state: RootState) =>
  state.config.settings?.express?.contact?.email;
export const selectConfigLocalContactPhysicalAddress = (state: RootState) =>
  state.config.settings?.express?.registration?.people?.local_contact
    ?.physical_address;
export const selectConfigLocalContactZipCodes = (state: RootState): number[] =>
  state.config.settings?.express?.registration?.people?.local_contact
    ?.physical_address?.allowed_postcodes ?? [];

export const SelectConfigSupportEmail = (state: RootState) =>
  state.config.settings?.support_contact; // Find new path to this
// The following verification needs to validate the upcoming data is a
// boolean true
export const selectConfigExpressHasMultipleOwners = (state: RootState) =>
  state.config.settings?.multiple_owners === true;

export const selectConfigExpressRegistrationPropertyManager = (
  state: RootState
) => state.config.settings?.express?.registration?.people?.property_manager;

export const selectConfigExpressRegistrationLocalContactOptionalFields = (
  state: RootState
) => state.config.settings?.express?.registration?.people?.local_contact;

export const selectConfigExpressRegistrationRegistrantPhysicalAddress = (
  state: RootState
) => state.config.settings?.express?.registration?.registrant?.physical_address;

export const selectConfigExpressRegistrationPeoplePropertyManagementDescription =
  (state: RootState) => {
    const propertyManagerText =
      state.config.settings?.express?.registration?.people?.property_manager
        ?.text;
    const rentalType = state.config.settings?.rentalType || RentalType.STR;
    return (
      propertyManagerText ||
      `Professional property management means a licensed firm charged with operating a real estate property for a fee. Self Managed means an individual maintains the ${rentalType.toUpperCase()}, such as a private property owner.`
    );
  };
export const selectConfigExpressRegistrationPeopleLocalContactDescription = (
  state: RootState
) => {
  const localContactText =
    state.config.settings?.express?.registration?.people?.local_contact?.text;
  const localContactVerbiage =
    state.config.settings?.express?.registration?.local_contact_verbiage;
  return (
    localContactText ||
    `A${beginsWithVowel(localContactVerbiage || '') ? 'n' : ''} ${localContactVerbiage?.toLowerCase()} is a person available 24/7 who can be at the property within one hour.`
  );
};

// APPLY CONFIG SELECTORS
export const selectConfigExpressRegistrantOptions = (state: RootState) =>
  state.config.settings?.express?.registration?.registrant;
export const selectConfigExpressApply = (state: RootState) =>
  state.config.settings?.express?.registration?.application;
export const selectConfigExpressApplyRequirements = (state: RootState) =>
  state.config.settings?.express?.registration?.application?.requirements;
export const selectConfigExpressApplyUnitNumberRequired = (state: RootState) =>
  state.config.settings?.express?.registration?.unit_number;
export const selectConfigExpressApplyUnitNumberDescription = (
  state: RootState
) => state.config.settings?.express?.registration?.unit_number_description;
export const selectConfigExpressApplyRentalStructureRequired = (
  state: RootState
) => state.config.settings?.express?.registration?.rental_structure;
export const selectConfigExpressApplyRentalStructure = (state: RootState) =>
  state.config.settings?.express?.registration?.rental_structure_options;
export const selectResolvedRentalStructureOptions = (state: RootState) =>
  resolveRentalStructureOptions(
    state.config.settings?.express?.registration?.rental_structure_options
  );
export const selectConfigExpressPropertyDetailsQuestions = (state: RootState) =>
  state.config.settings?.express?.registration?.application?.questions;
export const selectConfigExpressApplyDocuments = (state: RootState) =>
  state.config.settings?.express?.registration?.application?.documents;
export const selectConfigExpressRegistrationRenewEnabled = (state: RootState) =>
  state.config.settings?.express?.registration?.renew?.enable;
export const selectConfigExpressApplyCertifications = (state: RootState) =>
  state.config.settings?.express?.registration?.application?.certifications;
export const selectConfigExpressRegistrationMultiplePermitsPerAPNPolicy = (
  state: RootState
) =>
  state.config.settings?.express?.registration
    ?.multiple_permits_per_apn_policy ??
  MULTIPLE_PERMITS_PER_APN_POLICY_OPTIONS_ENUM.WARN;

// APPLY APPLICATION DATA SELECTORS
export const selectApplyApplicationData = (state: RootState) =>
  state.apply?.applicationData;

// UPDATE CONFIG SELECTORS
export const selectConfigExpressUpdate = (state: RootState) =>
  state.config.settings?.express?.registration?.update;
export const selectConfigExpressUpdatePropertyDetailsQuestions = (
  state: RootState
) => state.config.settings?.express?.registration?.update?.questions ?? [];
export const selectConfigExpressUpdateDocuments = (state: RootState) =>
  state.config.settings?.express?.registration?.update?.documents ?? [];
export const selectConfigExpressUpdateCertifications = (state: RootState) =>
  state.config.settings?.express?.registration?.update?.certifications;

// RENEW CONFIG SELECTORS
export const selectConfigExpressRenew = (state: RootState) =>
  state.config.settings?.express?.registration?.renew;
export const selectConfigExpressRenewRequirements = (state: RootState) =>
  state.config.settings?.express?.registration?.renew?.requirements;
export const selectConfigExpressRenewPropertyDetailsQuestions = (
  state: RootState
) => state.config.settings?.express?.registration?.renew?.questions;
export const selectConfigExpressRenewDocuments = (state: RootState) =>
  state.config.settings?.express?.registration?.renew?.documents;
export const selectConfigExpressRenewCertifications = (state: RootState) =>
  state.config.settings?.express?.registration?.renew?.certifications;
export const selectConfigExpressRenewLateFee = (state: RootState) =>
  state.config.settings?.express?.registration?.renew?.late_renewal_fee;
export const selectConfigExpressBulkRenewEnabled = (state: RootState) =>
  state.config.settings?.express?.registration?.bulk_renew_enabled;
// This property is not set in customer config, this property seems to be always false
// will alway be false original value was
// state.config.settings?.express?.registration?.legal_title_required;
export const selectConfigExpressLegalTitleRequired = () => false;

// TAX CERTIFICATE CONFIG SELECTORS
export const selectConfigExpressTaxCertificate = (state: RootState) =>
  state.config.settings?.express?.registration?.tax_certificate;
export const selectConfigExpressTaxCertificateEnabled = (state: RootState) =>
  state.config.settings?.express?.registration?.tax_certificate?.enable;
export const selectConfigExpressTaxCertificateRequirements = (
  state: RootState
) =>
  state.config.settings?.express?.registration?.tax_certificate?.requirements;
export const selectConfigExpressTaxCertificateCertifications = (
  state: RootState
) =>
  state.config.settings?.express?.registration?.tax_certificate?.certifications;
export const selectConfigExpressTaxCertificateSuccess = (state: RootState) =>
  state.config.settings?.express?.registration?.tax_certificate?.success;

// TAX CONFIG SELECTORS
export const selectConfigExpressTaxHidePropertyOccupancySection = (
  state: RootState
) => state.config.settings?.express?.tax?.hide_property_occupany_section;
export const selectConfigTaxPaymentPeriod = (state: RootState) => {
  return (
    state.config.settings?.totPaymentPeriod ||
    TAX_PAYMENT_PERIOD_TYPES_ENUM.MONTHLY
  );
};

export const selectConfigExpressTaxEnabled = (state: RootState) =>
  state.config.settings?.express?.tax?.enable;
export const selectConfigExpressTaxTaxableReceiptsLabel = (state: RootState) =>
  state.config.settings?.express?.tax?.taxable_receipts_label;

export const selectConfigExpressAllTaxCollectionAgreementWith = (
  state: RootState
): CollectionAgreementBucketWith => {
  return (
    state.config.settings?.express?.tax?.collection_agreement_bucket?.with || {}
  );
};

export const selectConfigExpressTax = (state: RootState) =>
  state.config.settings?.express?.tax as TaxConfiguration;
export const selectConfigExpressTaxVerbiage = (state: RootState) =>
  state.config.settings?.express?.tax?.verbiage;
export const deriveConfigExpressTaxCertifications = (
  state: RootState
): string[] =>
  state.config.settings?.express?.tax?.certifications || [
    C_DEFAULT_TAX_CERTIFICATION,
  ];

// PAYMENT SELECTORS (True by defauit if it does not exist)
export const selectConfigEnableCC = (state: RootState) =>
  state.config.settings?.enableCC ?? true;
export const selectConfigEnableACH = (state: RootState) =>
  state.config.settings?.enableACH ?? true;
// check is enabled if checkPaymentDetails exists
export const selectConfigEnableCheck = (state: RootState) => {
  const checkPaymentDetails = state.config.settings?.checkPaymentDetails;
  return checkPaymentDetails?.mailingAddress && checkPaymentDetails?.payTo;
};

export const selectConfigTaxCheckMailingAddress = (state: RootState) =>
  state.config.settings?.checkPaymentDetails?.mailingAddress;
export const selectConfigTaxCheckPayTo = (state: RootState) =>
  state.config.settings?.checkPaymentDetails?.payTo;

export const selectDownloadSummary = (state: RootState) => {
  return (
    state.config.settings?.express?.tax?.download_summary || {
      enable: false,
      label: '',
    }
  );
};

const DEFAULT_NUMBER_OF_PROPERTIES = 10;

export const selectNumberOfProperties = (state: RootState) => {
  return (
    state.config.settings?.express?.tax?.bulk_tax?.number_of_properties ||
    DEFAULT_NUMBER_OF_PROPERTIES
  );
};

export const selectBulkTax = (state: RootState) => {
  return state.config?.settings?.express?.tax?.bulk_tax?.enable || false;
};
